import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { settings } from "app";
import { ClassTypes } from "utils/constants";
import { FromWeekToWeekChoices, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import {
  validateFromToWeek,
  validateTimeEnd,
  validateTimeStart,
} from "components/LearningActivity/CreateWeeklySchedule/utils/utils";
import CurriculumStudyGroupsInput from "./components/CurriculumStudyGroupsInput";

const ClassTypeInput = props => {
  const { index } = useSimpleFormIteratorItem();
  const { setValue, getValues } = useFormContext();

  const onClassTypeChange = () => {
    getValues(`disciplineSchedules.${index}.weeklySchedule`)?.forEach((_, i) =>
      setValue(`disciplineSchedules.${index}.weeklySchedule.${i}.roomId`, null),
    );
  };

  return (
    <SelectInput
      {...props}
      onChange={onClassTypeChange}
    />
  );
};

const CreateForm = ({ isStored }) => {
  const { setValue, getValues } = useFormContext();

  const academicSessionType = useWatch({ name: "academicSessionType" });
  const studyTypeId = useWatch({ name: "studyTypeId" });
  const disciplineId = useWatch({ name: "disciplineId" });
  const curriculumIds = useWatch({ name: "curriculumIds" });

  const onDisciplineChange = () => {
    setValue("curriculumIds", []);
    setValue("instructionLanguageId", null);

    getValues("disciplineSchedules")?.forEach((schedule, i) =>
      schedule?.professors?.forEach((_, j) => setValue(`disciplineSchedules.${i}.professors.${j}.employeeId`, null)),
    );
  };

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          md={6}
          sm={6}
          xs={12}
        >
          <ReferenceInput
            source="academicYearId"
            reference={studentRequests.getAcademicYearsSelect()}
          >
            <SelectInput
              optionText="name"
              label="Академична година"
              readOnly={isStored}
              validate={[required()]}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          md={6}
          sm={6}
          xs={12}
        >
          <ReferenceInput
            source="studyTypeId"
            reference={administrationRequests.getStudyTypesSelect()}
          >
            <SelectInput
              optionText="name"
              label="Вид на обучение"
              readOnly={isStored}
              validate={[required()]}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          md={6}
          sm={6}
          xs={12}
        >
          <ReferenceInput
            source="academicSessionType"
            reference={studentRequests.getAcademicSessionTypesSelect()}
            filter={{ semestersOnly: true }}
          >
            <SelectInput
              optionText="name"
              label="Семестър"
              readOnly={isStored}
              validate={[required()]}
            />
          </ReferenceInput>
        </Grid>
        {academicSessionType && (
          <Grid
            item
            md={6}
            sm={6}
            xs={12}
          >
            <ReferenceInput
              source="disciplineId"
              reference={studentRequests.getDisciplinesSharedSelect()}
              filter={{
                semesterType: academicSessionType,
                studyTypeId,
                UsesElectiveAndFacultative: true,
              }}
            >
              <AutocompleteInput
                filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
                optionText="name"
                label="Дисциплина"
                validate={[required()]}
                onChange={onDisciplineChange}
              />
            </ReferenceInput>
          </Grid>
        )}
        {disciplineId && (
          <Grid
            item
            md={6}
            sm={6}
            xs={12}
          >
            <ReferenceArrayInput
              source="curriculumIds"
              reference={studentRequests.getCurriculumsSelect()}
              filter={{
                disciplineId,
                disciplineInSemesterType: academicSessionType,
                studyTypeId,
                UsesElectiveAndFacultative: true,
              }}
            >
              <SelectArrayInput
                label="Учебни планове"
                validate={[required()]}
              />
            </ReferenceArrayInput>
          </Grid>
        )}
        {curriculumIds?.length > 0 && (
          <Grid
            item
            md={6}
            sm={6}
            xs={12}
          >
            <ReferenceInput
              source="instructionLanguageId"
              reference={administrationRequests.getInstructionLanguagesSelect()}
            >
              <SelectInput
                label="Език на преподаване"
                validate={[required()]}
              />
            </ReferenceInput>
          </Grid>
        )}
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          md={12}
          sm={6}
          xs={12}
        >
          <ArrayInput
            source="disciplineSchedules"
            label="Разписания"
            defaultValue={[{}]}
          >
            <SimpleFormIterator
              disableReordering
              disableClear
              disableAdd
              sx={settings.style.iteratorWithoutGap}
            >
              {/* scopedFormData is the current disciplineSchedules row — useWatch cannot reach it without the row index. */}
              <FormDataConsumer>
                {({ scopedFormData }) => {
                  const defaultFromWeek = scopedFormData?.fromWeek;
                  const defaultТоWeek = scopedFormData?.toWeek;
                  return (
                    <>
                      <Grid
                        container
                        spacing={2}
                      >
                        <Grid
                          item
                          xs={12}
                          md={3}
                        >
                          <ReferenceInput
                            source="classTypeId"
                            reference={administrationRequests.getRoomClassTypesSelect()}
                            filter={{ excludeIds: [ClassTypes.Csc] }}
                          >
                            <ClassTypeInput
                              optionText="name"
                              label="Тип занятия"
                              validate={[required()]}
                            />
                          </ReferenceInput>
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          md={3}
                        >
                          <SelectInput
                            emptyText={false}
                            source="fromWeek"
                            label="От седмица"
                            choices={FromWeekToWeekChoices}
                            optionValue="id"
                            optionText="name"
                            validate={[required(), () => validateFromToWeek(scopedFormData)]}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={3}
                        >
                          <SelectInput
                            emptyText={false}
                            source="toWeek"
                            label="До седмица"
                            choices={FromWeekToWeekChoices}
                            optionValue="id"
                            optionText="name"
                            validate={[required(), () => validateFromToWeek(scopedFormData)]}
                          />
                        </Grid>
                      </Grid>

                      {disciplineId && (
                        <ArrayInput
                          source="professors"
                          label="Преподаватели"
                          defaultValue={[{}]}
                        >
                          <SimpleFormIterator
                            disableReordering
                            disableClear
                            sx={settings.style.iteratorWithoutGap}
                          >
                            <ProfessorInput
                              source="employeeId"
                              label="Преподавател"
                              fullWidth
                              validate={[required()]}
                              filters={{ disciplineId }}
                            />
                            <Grid
                              container
                              spacing={2}
                            >
                              <Grid
                                item
                                xs={12}
                                md={6}
                              >
                                <SelectInput
                                  emptyText={false}
                                  source="fromWeek"
                                  label="От седмица"
                                  choices={FromWeekToWeekChoices}
                                  optionValue="id"
                                  optionText="name"
                                  defaultValue={defaultFromWeek}
                                  validate={[required()]}
                                />
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={6}
                              >
                                <SelectInput
                                  emptyText={false}
                                  source="toWeek"
                                  label="До седмица"
                                  choices={FromWeekToWeekChoices}
                                  optionValue="id"
                                  optionText="name"
                                  defaultValue={defaultТоWeek}
                                  validate={[required()]}
                                />
                              </Grid>
                            </Grid>
                          </SimpleFormIterator>
                        </ArrayInput>
                      )}
                      <ArrayInput
                        source="weeklySchedule"
                        label="Седмичен график"
                        defaultValue={[{}]}
                      >
                        <SimpleFormIterator
                          disableReordering
                          disableClear
                          sx={settings.style.iteratorWithoutGap}
                        >
                          <Grid
                            container
                            spacing={2}
                          >
                            <RadioButtonGroupInput
                              label="Тип седмица"
                              source="weekType"
                              choices={WeekTypeChoices}
                              validate={required()}
                            />
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <SelectInput
                                source="weekDay"
                                label="Ден от седмицата"
                                choices={WeekDaysChoices}
                                optionValue="id"
                                optionText="name"
                                validate={[required()]}
                              />
                            </Grid>
                            {scopedFormData?.classTypeId && (
                              <Grid
                                item
                                xs={12}
                                md={8}
                              >
                                <ReferenceInput
                                  source="roomId"
                                  reference={administrationRequests.getRoomsSelect()}
                                  filter={{ classTypes: scopedFormData.classTypeId }}
                                >
                                  <AutocompleteInput
                                    label="Зала"
                                    optionValue="id"
                                    optionText={rec =>
                                      `${rec.roomName} (Номер на зала: ${rec.roomNumber || ""}) ${
                                        rec.buildingName
                                      } (Капацитет занятия: ${rec.capacityClasses || ""})`
                                    }
                                    filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                                    fullWidth
                                    validate={[required()]}
                                  />
                                </ReferenceInput>
                              </Grid>
                            )}
                          </Grid>
                          <Grid
                            container
                            spacing={2}
                            sx={{ mb: 3 }}
                          >
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              {/* Nested consumer: scopedFormData here is the weeklySchedule row, not the schedule above. */}
                              <FormDataConsumer>
                                {({ scopedFormData }) => (
                                  <CustomTimeOnlyInput
                                    source="startTime"
                                    label="Начален час"
                                    validate={() => validateTimeStart(scopedFormData)}
                                    required
                                  />
                                )}
                              </FormDataConsumer>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={4}
                            >
                              <FormDataConsumer>
                                {({ scopedFormData }) => (
                                  <CustomTimeOnlyInput
                                    source="endTime"
                                    label="Краен час"
                                    validate={() => validateTimeEnd(scopedFormData)}
                                    required
                                  />
                                )}
                              </FormDataConsumer>
                            </Grid>
                          </Grid>
                          {/* Nested consumer: scopedFormData here is the weeklySchedule slot. */}
                          <FormDataConsumer>
                            {({ scopedFormData: slotData }) => (
                              <Grid
                                container
                                spacing={2}
                              >
                                {curriculumIds?.length > 0 && (
                                  <Grid
                                    item
                                    xs={12}
                                  >
                                    <BooleanInput
                                      source="perCurriculumStudyGroups"
                                      label="Различни учебни групи по учебен план"
                                      helperText={false}
                                    />
                                  </Grid>
                                )}
                                {slotData?.perCurriculumStudyGroups && curriculumIds?.length > 0 ? (
                                  <Grid
                                    item
                                    xs={12}
                                  >
                                    <CurriculumStudyGroupsInput curriculumIds={curriculumIds} />
                                  </Grid>
                                ) : (
                                  <Grid
                                    item
                                    xs={12}
                                    md={4}
                                  >
                                    <StudyGroupField
                                      label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
        групи!"
                                    />
                                  </Grid>
                                )}
                              </Grid>
                            )}
                          </FormDataConsumer>
                        </SimpleFormIterator>
                      </ArrayInput>
                    </>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    </>
  );
};

export default CreateForm;
